import { PICTOGRAM, PictogramKey } from "encamp-shared/src/waste/dotPictograms";
import {
  HazardLabelDesign,
  HAZARD_LABEL_CORNER_RADIUS_RATIO,
  HAZARD_LABEL_INNER_BORDER_WIDTH_RATIO,
  HAZARD_LABEL_NUMBER_TOP_RATIO,
  HAZARD_LABEL_UNDERLINE_GAP_RATIO,
  HAZARD_LABEL_UNDERLINE_THICKNESS_RATIO,
  HAZARD_LABEL_UNDERLINE_WIDTH_RATIO,
  HAZARD_LABEL_OUTLINE_RATIO,
  hazardLabelNumberSize,
} from "encamp-shared/src/waste/hazardClassDiamond";
import {
  diamondFills,
  diamondPath,
  HAZARD_LABEL_OUTLINE_COLOR,
  hazardLabelSymbolBox,
  innerBorderColor,
  innerBorderOutsidePath,
  innerBorderPath,
} from "encamp-shared/src/waste/hazardLabelGeometry";
import { ReactElement } from "react";

/**
 * Oak's DOM-SVG mirror of the printed label's `HazardLabelDiamond`
 * (apps/lambdas/pdf-generator/src/generators/hazardousWasteLabelDiamond.tsx).
 * Oak cannot import that file -- it renders with `@react-pdf/renderer`
 * primitives, which do not exist outside pdf-generator. Only the element tree
 * differs: the shapes and colours both draw come from
 * encamp-shared/waste/hazardLabelGeometry, so the two cannot drift.
 */

/**
 * The symbol in the diamond's upper half. Not rotated with the background --
 * a rotated symbol would be lying on its side. Mirrors
 * in hazardousWasteLabelDiamond.tsx.
 */
function HazardLabelSymbol({
  design,
  size,
}: {
  design: HazardLabelDesign;
  size: number;
}): ReactElement | null {
  if (!design.pictogram) return null;
  const pictogram = PICTOGRAM[design.pictogram];
  const color = design.pictogramColor ?? design.textColor;
  const box = hazardLabelSymbolBox(
    design,
    size,
    pictogram.viewBox.width / pictogram.viewBox.height
  );
  return (
    <svg
      width={box.width}
      height={box.height}
      // Every pictogram is cropped to its own bounding box inside the source
      // design's coordinate space (dotPictograms.ts), so the viewBox must
      // carry the symbol's minX/minY -- "0 0 W H" puts the symbol outside the
      // viewport and clips it to a sliver. This bug was introduced twice on
      // the PDF side before being caught by a rendered-image check.
      viewBox={`${pictogram.viewBox.minX} ${pictogram.viewBox.minY} ${pictogram.viewBox.width} ${pictogram.viewBox.height}`}
      style={{
        position: "absolute",
        top: box.top,
        left: box.left,
      }}
    >
      {pictogram.paths.map((path, index) => (
        <path
          key={index}
          d={path.d}
          fill={path.fill ?? color}
          fillRule={path.fillRule}
          stroke={path.stroke}
          strokeWidth={path.strokeWidth}
        />
      ))}
    </svg>
  );
}

/**
 * One DOT design: the diamond, its background, its symbol, and its class
 * number. Mirrors `HazardLabelDiamond` in hazardousWasteLabelDiamond.tsx.
 * 49 CFR 172.402(b) requires the number on primary and subsidiary labels
 * alike. The number is drawn only when `cornerNumber` is given.
 */
export function PreviewHazardLabel({
  design,
  cornerNumber,
  size,
  numberSize,
}: {
  design: HazardLabelDesign;
  cornerNumber?: string;
  size: number;
  numberSize: number;
}): ReactElement {
  // The preview prints no label text, so the number keeps its usual place.
  const numberTopRatio = HAZARD_LABEL_NUMBER_TOP_RATIO;
  const fontSize = cornerNumber
    ? hazardLabelNumberSize(cornerNumber, size, numberSize, numberTopRatio)
    : numberSize;
  const cornerRadius = size * HAZARD_LABEL_CORNER_RADIUS_RATIO;
  const outline = diamondPath(size, cornerRadius);
  // Two divisions of one class share a corner number (2.1 and 2.3 are both
  // "2"), so the symbol is part of the id too.
  const clipId = `design-${size}-${design.background.kind}-${design.pictogram ?? "none"}-${cornerNumber ?? "sub"}`;
  const innerOutline = innerBorderPath(
    size,
    HAZARD_LABEL_CORNER_RADIUS_RATIO,
    design.innerBorderLowerHalfOnly === true
  );
  return (
    <div
      data-testid="label-preview-hazard-diamond"
      style={{ position: "relative", width: size, height: size }}
    >
      <svg width={size} height={size}>
        <defs>
          <clipPath id={clipId}>
            <path d={outline} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${clipId})`}>
          {diamondFills(
            design.background,
            size,
            innerBorderOutsidePath(size, HAZARD_LABEL_CORNER_RADIUS_RATIO)
          ).map((fill, index) => (
            <path key={index} d={fill.d} fill={fill.color} data-testid="design-fill" />
          ))}
        </g>
        {/* 172.406(d): a solid outer border on every label, because the
            package colour it goes on is unknown. */}
        <path
          d={outline}
          fill="none"
          stroke={HAZARD_LABEL_OUTLINE_COLOR}
          strokeWidth={size * HAZARD_LABEL_OUTLINE_RATIO}
        />
        {/* 172.407(c)'s inner border. */}
        <path
          d={innerOutline}
          fill="none"
          stroke={innerBorderColor(design)}
          strokeWidth={size * HAZARD_LABEL_INNER_BORDER_WIDTH_RATIO}
          data-testid="hazard-label-inner-border"
        />
      </svg>
      <HazardLabelSymbol design={design} size={size} />
      {cornerNumber ? (
        <div
          style={{
            position: "absolute",
            top: size * numberTopRatio,
            left: 0,
            width: size,
            textAlign: "center",
            // Both set here: the line box takes its height from the
            // wrapper's own font, so a page-size font would push a smaller
            // number down onto the inner border.
            fontSize,
            lineHeight: 1,
          }}
        >
          <span
            style={{
              color: design.textColor,
              fontWeight: 700,
              fontSize,
            }}
          >
            {cornerNumber}
          </span>
          {design.underlineNumber === true ? (
            <div
              data-testid="hazard-label-number-underline"
              style={{
                margin: `${fontSize * HAZARD_LABEL_UNDERLINE_GAP_RATIO}px auto 0`,
                width: fontSize * HAZARD_LABEL_UNDERLINE_WIDTH_RATIO,
                height: fontSize * HAZARD_LABEL_UNDERLINE_THICKNESS_RATIO,
                backgroundColor: design.textColor,
              }}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/**
 * One RCRA characteristic symbol, sized by HEIGHT with width following the
 * symbol's own aspect. Mirrors `RcraPictogram` in
 * hazardousWasteLabelComponents.tsx:293-313.
 */
export function RcraPictogram({
  pictogram,
  height,
}: {
  pictogram: PictogramKey;
  height: number;
}): ReactElement {
  const { viewBox, paths } = PICTOGRAM[pictogram];
  const width = height * (viewBox.width / viewBox.height);
  return (
    <svg
      width={width}
      height={height}
      viewBox={`${viewBox.minX} ${viewBox.minY} ${viewBox.width} ${viewBox.height}`}
    >
      {paths.map((path, index) => (
        <path
          key={index}
          d={path.d}
          fill={path.fill ?? "#000000"}
          fillRule={path.fillRule}
          stroke={path.stroke}
          strokeWidth={path.strokeWidth}
        />
      ))}
    </svg>
  );
}

/**
 * The 49 CFR 172.312 package orientation mark: two upward arrows, shown on a
 * combination packaging containing liquid. Mirrors `OrientationArrows` in
 * hazardousWasteLabelDiamond.tsx:246-259.
 */
export function OrientationArrows({ size }: { size: number }): ReactElement {
  const pictogram = PICTOGRAM.ORIENTATION_ARROWS;
  return (
    <svg
      data-testid="label-preview-orientation-arrows"
      width={size}
      height={size}
      viewBox={`${pictogram.viewBox.minX} ${pictogram.viewBox.minY} ${pictogram.viewBox.width} ${pictogram.viewBox.height}`}
    >
      {pictogram.paths.map((path, index) => (
        <path key={index} d={path.d} fill={path.fill ?? "#000000"} />
      ))}
    </svg>
  );
}
